"use client";

import {
  createContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { usePathname } from "next/navigation";

type Props = {
  children: ReactNode;
  className?: string;
};

type ScrollHeaderRevealState = {
  isHydrated: boolean;
  revealed: boolean;
};

export const ScrollHeaderRevealContext =
  createContext<ScrollHeaderRevealState | null>(null);

const subscribeToHydration = () => () => {};
const getHydratedSnapshot = () => true;
const getServerSnapshot = () => false;

const HIDDEN = { opacity: 0, y: 14, filter: "blur(6px)" };
const VISIBLE = { opacity: 1, y: 0, filter: "blur(0px)" };
const REVEAL_TRANSITION = { duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] };
const INSTANT_TRANSITION = { duration: 0 };

// Soft navigation in the App Router can preserve the home page subtree, so an
// IntersectionObserver that disconnected after firing stays disconnected on
// return. Keying the inner by `usePathname()` forces a fresh mount on every
// route change — fresh state, fresh observer, fresh reveal.
export function ScrollReanimateHeader({ children, className }: Props) {
  const pathname = usePathname();
  return (
    <ScrollReanimateHeaderInner key={pathname} className={className}>
      {children}
    </ScrollReanimateHeaderInner>
  );
}

function ScrollReanimateHeaderInner({ children, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydratedSnapshot,
    getServerSnapshot,
  );
  const reduceMotion = useReducedMotion();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (reduceMotion) {
      setRevealed(true);
      return;
    }
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -18% 0px", threshold: 0.35 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [reduceMotion]);

  // Pre-hydration server output uses `initial={false}` + animate=VISIBLE, so
  // SSR HTML has no opacity/blur styles (crawlers and no-JS clients see the
  // text). Post-hydration we flip to HIDDEN with duration 0, then animate to
  // VISIBLE once the IntersectionObserver fires.
  const shouldHide = isHydrated && !revealed && !reduceMotion;

  return (
    <div ref={ref} className={className}>
      <ScrollHeaderRevealContext.Provider value={{ isHydrated, revealed }}>
        <motion.div
          initial={false}
          animate={shouldHide ? HIDDEN : VISIBLE}
          transition={shouldHide ? INSTANT_TRANSITION : REVEAL_TRANSITION}
        >
          {children}
        </motion.div>
      </ScrollHeaderRevealContext.Provider>
    </div>
  );
}
